// @flow
import {fabric} from 'fabric';

import type {BiePluginType} from '../types';
import {CanvasManager} from '../managers/canvas-manager';

export class BieSquarePlugin implements BiePluginType {
  cm: CanvasManager;
  mode: string = 'waitForClick';
  objectsCount: number;

  tempStartPoint = {x: -1, y: -1};
  currentShape: fabric.Rect | fabric.Ellipse;

  currentColor: string;
  currentShapeType: string;

  setup() {}

  getActionButtons() {
    return [
      {
        type: 'enter-state',
        title: 'Draw Shape',
        icon: 'far fa-square',
        slug: 'shape',
        component: 'div',
        enter: this.start,
        cancel: this.cancel,
        confirm: this.confirm,
        triggerEvent: this.handleEvent,
        eventButtons: [
          {
            type: 'shapeSelect',
          },
          {
            type: 'colorPalette',
          },
        ],
      },
    ];
  }

  handleEvent = (cm: CanvasManager, event: any) => {
    if (event.source === 'colorPalette') {
      this.currentColor = event.type;
    } else if (event.source === 'shapeSelect') {
      this.currentShapeType = event.type;
    }
  };

  removeListeners(cm: CanvasManager) {
    cm.canvas.off('mouse:down', this.onMouseDown);
    cm.canvas.off('mouse:over', this.onObjectOver);
    cm.canvas.off('mouse:out', this.onObjectOut);
    cm.canvas.off('mouse:up', this.onMouseUp);
    cm.canvas.off('mouse:move', this.onMouseMove);

    cm.canvas.hoverCursor = 'move';
    cm.canvas.selection = true;
  }

  cancel = (cm: CanvasManager) => {
    this.removeListeners(cm);
    cm.canvas
      .getObjects()
      .slice(this.objectsCount)
      .map((object) => cm.canvas.remove(object));
    cm.canvas.renderAll();
  };

  confirm = (cm: CanvasManager) => {
    this.removeListeners(cm);
    cm.saveState();
  };

  start = (cm: CanvasManager) => {
    this.cm = cm;

    this.objectsCount = cm.canvas.getObjects().length;
    this.currentColor = 'red';
    this.currentShapeType = 'square';

    cm.canvas.on('mouse:down', this.onMouseDown);
    cm.canvas.on('mouse:over', this.onObjectOver);
    cm.canvas.on('mouse:out', this.onObjectOut);
    cm.canvas.on('mouse:up', this.onMouseUp);
    cm.canvas.on('mouse:move', this.onMouseMove);
    cm.canvas.hoverCursor = 'crosshair';

    cm.canvas.selection = false;
  };

  onObjectOver = (event) => {
    if (
      event.target != null &&
      event.target.type !== 'image' &&
      this.mode === 'waitForClick'
    ) {
      this.mode = 'overObject';
      this.cm.canvas.selection = true;
    }
  };

  onObjectOut = (event) => {
    if (
      event.target != null &&
      event.target.type !== 'image' &&
      this.mode === 'overObject'
    ) {
      this.mode = 'waitForClick';
      this.cm.canvas.selection = false;
    }
  };

  onMouseDown = (event) => {
    if (
      this.cm.canvas.getActiveObject() != null ||
      this.mode === 'overObject'
    ) {
      return;
    }

    this.mode = 'tempDrawShape';
    this.tempStartPoint.x = event.absolutePointer.x;
    this.tempStartPoint.y = event.absolutePointer.y;
  };

  onMouseMove = (event) => {
    if (this.mode === 'tempDrawShape') {
      let x = event.absolutePointer.x;
      let y = event.absolutePointer.y;

      let width = this.tempStartPoint.x - event.absolutePointer.x;
      if (width < 0) {
        width = Math.abs(width);
        x = x - width;
      }

      let height = this.tempStartPoint.y - event.absolutePointer.y;
      if (height < 0) {
        height = Math.abs(height);
        y = y - height;
      }

      if (this.currentShape == null) {
        if (Math.sqrt(width * width + height * height) < 10) {
          return;
        }

        if (this.currentShapeType === 'square') {
          this.currentShape = new fabric.Rect({
            width: width,
            height: height,
            left: x,
            top: y,
            stroke: this.currentColor,
            strokeWidth: 12,
            fill: null,
            strokeUniform: true,
          });
        } else if (this.currentShapeType === 'circle') {
          this.currentShape = new fabric.Ellipse({
            rx: width / 2,
            ry: height / 2,
            left: x,
            top: y,
            stroke: this.currentColor,
            strokeWidth: 12,
            fill: null,
            strokeUniform: true,
          });
        }

        this.cm.canvas.add(this.currentShape);
        this.cm.canvas.renderAll();
      } else {
        if (this.currentShapeType === 'square') {
          this.currentShape.set({
            width: width,
            height: height,
            left: x,
            top: y,
          });
        } else if (this.currentShapeType === 'circle') {
          this.currentShape.set({
            rx: width / 2,
            ry: height / 2,
            left: x,
            top: y,
          });
        }
        this.cm.canvas.renderAll();
      }
    }
  };

  onMouseUp = () => {
    this.mode = 'waitForClick';

    if (this.mode === 'overObject' || this.currentShape == null) {
      return;
    }
    this.cm.canvas.remove(this.currentShape);
    this.cm.canvas.add(this.currentShape);
    this.cm.canvas.renderAll();
    this.currentShape = null;
  };
}
